import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { statusLabel, type Task } from '../tasks'
import {
  Plus, X, Film, Image as ImageIcon, FileText, Play, Loader2, RefreshCw, Trash2, Pencil,
  Smartphone,
} from 'lucide-react'
import QRCode from 'qrcode'
import { api, ApiError, mediaURL } from '../api'
import type { Brand, Asset, Role, Template, UploadResult } from '../types'
import { atLeast } from '../types'
import { AuthImage, FilePicker } from '../components'
import {
  Button, Card, CardTitle, ConfirmButton, EmptyState, Field, Input, Textarea, Note,
  Menu, SectionLabel, ErrorText, Skeleton, Dialog,
} from '../components/ui'
import { Tabs } from '../components/select'
import { ago, cn } from '../lib/utils'
import { Templates } from './Templates'

// Assets belong to the brand in the database, but a workspace has exactly one
// brand -- so this resolves it rather than asking, the same way the ad builder
// does. The API keeps the brand in the path because that is where the rows
// actually hang.

// The stored value stays 'insert' -- it is the API contract and the database
// check constraint. Only the label changes, because "product demo" is what
// this actually is to the person uploading one.
const KINDS = {
  insert: {
    label: 'Product demo',
    blurb: 'Recorded footage of the product, spliced into the middle of a video.',
    empty: 'Record your screen showing the product, and it can be spliced into the middle of a video in the avatar\u2019s voice.',
    icon: Film,
    accept: 'video/*',
  },
  environment: {
    label: 'Environment',
    blurb: 'A screenshot of a real video, used to lift a setting for a new look.',
    empty: 'A screenshot of a real social video is what a new look lifts its room, lighting and framing from. Interface and all \u2014 that gets removed automatically.',
    icon: ImageIcon,
    accept: 'image/*',
  },
} as const

// Environment references are uploaded directly inside look creation. Keeping
// a second saved library here created assets that no workflow ever selected.
const VISIBLE_MEDIA_TABS: Array<keyof typeof KINDS> = ['insert']

type TemplateTab = Exclude<Template['kind'], 'bridge'>
type AssetTab = keyof typeof KINDS | TemplateTab

export function Assets({ ws, role, initialTab = 'insert' }: {
  ws: string
  role?: Role
  initialTab?: AssetTab
}) {
  const [adding, setAdding] = useState(false)
  const [tab, setTab] = useState<AssetTab>(initialTab)
  const canEdit = atLeast(role, 'member')

  useEffect(() => {
    setTab(initialTab)
    setAdding(false)
  }, [initialTab])

  const brand = useQuery({
    queryKey: ['brand', ws],
    queryFn: async () => {
      try {
        return await api.get<Brand>(`/api/workspaces/${ws}/brand`)
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },
  })

  const assets = useQuery({
    queryKey: ['assets', ws, brand.data?.id],
    queryFn: () => api.get<Asset[]>(`/api/workspaces/${ws}/brands/${brand.data!.id}/assets`),
    enabled: !!brand.data,
  })

  // Shares its cache with Templates, so the tab counts and selected list come
  // from one request rather than two independent fetches.
  const templates = useQuery({
    queryKey: ['templates', ws],
    queryFn: () => api.get<Template[]>(`/api/workspaces/${ws}/templates`),
  })

  if (brand.isLoading) return <Skeleton className="h-40 w-full" />
  if (brand.error) return <ErrorText error={brand.error} />

  if (!brand.data) {
    return (
      <EmptyState
        icon={Film}
        title="No brand yet"
        body="Assets belong to this workspace's brand. Set one up under Brand Context first."
      />
    )
  }

  const rows = assets.data ?? []
  const templateRows = templates.data ?? []
  const mediaTab = tab === 'insert' || tab === 'environment' ? tab : null
  const templateTab = mediaTab ? null : tab as TemplateTab
  const shown = mediaTab ? rows.filter(a => a.kind === mediaTab) : []
  const meta = mediaTab ? KINDS[mediaTab] : null

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={tab}
          onValueChange={v => { setTab(v as AssetTab); setAdding(false) }}
          tabs={[
            ...VISIBLE_MEDIA_TABS.map(value => ({
              value,
              label: `${KINDS[value].label}s`,
              count: rows.filter(a => a.kind === value).length,
            })),
            { value: 'hook', label: 'Hooks', count: templateRows.filter(t => t.kind === 'hook').length },
            { value: 'cta', label: 'CTAs', count: templateRows.filter(t => t.kind === 'cta').length },
          ]}
        />
        {canEdit && (meta || templateTab) && (
          <Button variant={adding ? 'ghost' : 'primary'} onClick={() => setAdding(v => !v)}>
            {adding
              ? <><X /> Cancel</>
              : <><Plus /> {meta
                ? `Upload ${meta.label.toLowerCase()}`
                : `New ${templateTab === 'cta' ? 'CTA' : templateTab}`}</>}
          </Button>
        )}
      </div>

      {templateTab ? (
        <Templates
          ws={ws} role={role} kind={templateTab}
          adding={adding} onAddingChange={setAdding} hideAction
        />
      ) : (
        <>
          <p className="text-xs text-ink-faint">{meta!.blurb}</p>

          {adding && (
            // Opens on the kind you are already looking at, rather than making
            // you set it twice.
            <NewAsset
              ws={ws} brandID={brand.data.id} initialKind={mediaTab!}
              onDone={() => setAdding(false)}
            />
          )}

          <ErrorText error={assets.error} />
          {assets.isLoading && <Skeleton className="h-32 w-full" />}

          {!assets.isLoading && shown.length === 0 && !adding && (
            <EmptyState
              icon={meta!.icon}
              title={`No ${meta!.label.toLowerCase()}s yet`}
              body={meta!.empty}
              action={canEdit
                ? (
                  <Button variant="primary" onClick={() => setAdding(true)}>
                    <Plus /> Upload {meta!.label.toLowerCase()}
                  </Button>
                )
                : undefined}
            />
          )}

          {shown.length > 0 && <AssetGrid ws={ws} kind={mediaTab!} assets={shown} canEdit={canEdit} />}
        </>
      )}
    </div>
  )
}

function AssetGrid({ ws, kind, assets, canEdit }: {
  ws: string
  kind: keyof typeof KINDS
  assets: Asset[]
  canEdit: boolean
}) {
  const qc = useQueryClient()

  // Which demos are being transcribed right now.
  //
  // Uploading queues transcription automatically, and nothing said so: the tile
  // read "no transcript" and stayed that way until something else happened to
  // refetch. Read from the task list rather than remembered from the upload
  // response, so it survives a reload and covers work queued from anywhere.
  const tasks = useQuery({
    queryKey: ['tasks', ws],
    queryFn: () => api.get<Task<unknown, { asset_id?: string }>[]>(
      `/api/workspaces/${ws}/tasks`),
    // Polls quickly while something is running, and slowly otherwise. The slow
    // tick is what catches work queued from elsewhere -- another tab, or the
    // upload that just finished -- without asking every second forever.
    refetchInterval: q => {
      const live = (q.state.data ?? []).some(
        t => t.kind === 'transcribe_asset' && (t.status === 'queued' || t.status === 'running'))
      return live ? 1500 : 8000
    },
  })

  const live = new Map<string, Task<unknown, { asset_id?: string }>>(
    (tasks.data ?? [])
      .filter(t => t.kind === 'transcribe_asset'
        && (t.status === 'queued' || t.status === 'running')
        && t.input?.asset_id)
      .map(t => [t.input.asset_id as string, t]),
  )
  const transcribing = live

  // When the last one finishes, pick up the text it produced.
  const liveCount = live.size
  useEffect(() => {
    if (liveCount === 0) qc.invalidateQueries({ queryKey: ['assets', ws] })
  }, [liveCount, qc, ws])
  // The transcript editor lives here rather than in the tile. A tile is a
  // quarter of the width, and a 200-word transcript in a column that narrow is
  // technically editable and practically not.
  const [editingID, setEditingID] = useState('')
  const editing = assets.find(a => a.id === editingID)

  // Both kinds render as pictures. You recognise a recording by looking at
  // it -- a name someone typed in a hurry and a generic film icon are not
  // enough to tell two demos apart.
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {assets.map(a => (
          <AssetTile
            key={a.id} ws={ws} asset={a} kind={kind}
            selected={a.id === editingID}
            transcribing={transcribing.has(a.id)}
            canEdit={canEdit}
            onEdit={() => setEditingID(id => (id === a.id ? '' : a.id))}
          />
        ))}
      </div>

      {editing && (
        <TranscriptEditor
          ws={ws} asset={editing} liveTask={live.get(editing.id)}
          onClose={() => setEditingID('')}
        />
      )}
    </div>
  )
}

function AssetTile({ ws, asset, kind, selected, transcribing, canEdit, onEdit }: {
  ws: string
  asset: Asset
  kind: keyof typeof KINDS
  selected: boolean
  transcribing: boolean
  canEdit: boolean
  onEdit: () => void
}) {
  const qc = useQueryClient()
  const [playing, setPlaying] = useState(false)
  const [videoURL, setVideoURL] = useState('')
  const [failed, setFailed] = useState('')
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(asset.name)

  const remove = useMutation({
    mutationFn: () => api.del(`/api/workspaces/${ws}/assets/${asset.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assets', ws] }),
  })
  const rename = useMutation({
    mutationFn: () => api.patch(`/api/workspaces/${ws}/assets/${asset.id}`, { name: name.trim() }),
    onSuccess: () => {
      setRenaming(false)
      qc.invalidateQueries({ queryKey: ['assets', ws] })
    },
  })

  // A signed URL rather than a downloaded blob, so the browser streams and can
  // seek. Fetching the whole file first meant a large demo sat doing nothing
  // visible for as long as it took to arrive.
  useEffect(() => {
    if (!playing || videoURL) return
    let dead = false
    mediaURL(ws, asset.storage_key)
      .then(u => { if (!dead) setVideoURL(u) })
      .catch(() => { if (!dead) { setFailed('Could not load this video'); setPlaying(false) } })
    return () => { dead = true }
  }, [playing, videoURL, ws, asset.storage_key])

  const isVideo = kind === 'insert'
  // A video without a poster still shows something rather than nothing.
  const thumbKey = isVideo ? asset.poster_key : asset.storage_key

  return (
    <figure className="group space-y-2">
      <div className="relative overflow-hidden rounded-[--radius-control] border border-line bg-surface-2">
        {playing ? (
          videoURL ? (
            <video
              src={videoURL} controls autoPlay playsInline
              className="aspect-[9/16] w-full bg-black object-contain"
              onError={() => { setFailed('Could not play this video'); setPlaying(false) }}
            />
          ) : (
            // The gap between click and playable URL is short, but showing the
            // poster again during it is what made the button look dead.
            <div className="grid aspect-[9/16] w-full place-items-center bg-black">
              <Loader2 className="size-6 animate-spin text-brand" />
            </div>
          )
        ) : (
          <>
            <AuthImage
              ws={ws} storageKey={thumbKey} alt={asset.name}
              ratio="portrait" className="w-full border-0"
            />
            {isVideo && (
              <button
                onClick={() => setPlaying(true)}
                className="absolute inset-0 grid place-items-center bg-black/25 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                aria-label={`Play ${asset.name}`}
              >
                <span className="grid size-11 place-items-center rounded-full bg-brand text-brand-ink shadow-lg">
                  <Play className="size-5 translate-x-px" />
                </span>
              </button>
            )}
            {isVideo && asset.duration_s != null && (
              <span className="absolute bottom-1.5 right-1.5 rounded bg-black/75 px-1.5 py-0.5 text-[11px] tabular-nums text-white">
                {formatDuration(asset.duration_s)}
              </span>
            )}
          </>
        )}
      </div>

      <figcaption className="space-y-1">
        <div className="flex items-start gap-1">
          <div className="min-w-0 flex-1 truncate text-sm font-medium" title={asset.name}>
            {asset.name}
          </div>
          {/* Both actions live behind the kebab. Editing a transcript and
              deleting a file are occasional, and two icons per tile in a grid
              of them is more chrome than the thumbnails can carry. */}
          {canEdit && <Menu
            className="-mt-1 shrink-0"
            label={`Actions for ${asset.name}`}
            actions={[
              ...(isVideo
                ? [{
                    label: asset.transcript ? 'Edit transcript' : 'Add transcript',
                    icon: FileText,
                    onSelect: onEdit,
                  }]
                : []),
              {
                label: 'Rename',
                icon: Pencil,
                onSelect: () => { setName(asset.name); setRenaming(true) },
              },
              {
                label: 'Delete',
                icon: Trash2,
                danger: true,
                disabled: remove.isPending,
                // Names the file, because an uploaded recording cannot be
                // regenerated from anything in here.
                confirmLabel: `Delete ${asset.name}? The file goes with it.`,
                onSelect: () => remove.mutate(),
              },
            ]}
          />}
        </div>
        <ErrorText error={remove.error} />
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-ink-faint">
          {failed ? <span className="text-bad">{failed}</span> : <span>{ago(asset.created_at)}</span>}
          {isVideo && (
            // A button, not a badge. These words are burned into the captions
            // verbatim, so the state of the transcript is worth surfacing AND
            // worth being one click from fixing.
            transcribing ? (
              // Said plainly while it happens. Uploading starts this on its
              // own, and a tile that says "no transcript" during it looks like
              // a failure rather than a few seconds of work.
              <span className="flex items-center gap-1 text-ink-faint">
                <Loader2 className="size-3 animate-spin text-brand" /> transcribing…
              </span>
            ) : (
              <button
                onClick={onEdit}
                className={cn('flex items-center gap-1 underline-offset-2 hover:underline',
                  asset.transcript ? 'text-good' : 'text-warn',
                  selected && 'underline')}
              >
                <FileText className="size-3" />
                {asset.transcript ? 'transcript' : 'no transcript'}
              </button>
            )
          )}
        </div>
      </figcaption>
      <Dialog open={renaming} onOpenChange={setRenaming} title="Rename product demo">
        <div className="space-y-4">
          <Field label="Name">
            <Input value={name} onChange={event => setName(event.target.value)} autoFocus />
          </Field>
          <ErrorText error={rename.error} />
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => rename.mutate()} disabled={!name.trim() || rename.isPending}>
              {rename.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="ghost" onClick={() => setRenaming(false)}>Cancel</Button>
          </div>
        </div>
      </Dialog>
    </figure>
  )
}

/** What the transcription task reports back. */
type TranscribeResult = {
  transcript: string
  words: number
  duration_s: number
  silent?: string
  skipped?: string
}

/** The transcript, editable.
 *
 *  Whisper is good but not right: on a real demo here it produced "app cont ent
 *  st ud io io" and "listening for Brad Mon it ring". Those are not
 *  transcription curiosities, they are what gets burned onto the screen, so
 *  correcting them is part of uploading a demo rather than an advanced option. */
function TranscriptEditor({ ws, asset, liveTask, onClose }: {
  ws: string
  asset: Asset
  /** A transcription already running for this asset -- queued by the upload,
   *  or from another tab. Treated exactly like one started from this panel:
   *  the text is about to be replaced either way, so the box locks and says
   *  what is happening rather than inviting an edit that will be overwritten. */
  liveTask?: Task<unknown, { asset_id?: string }>
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [text, setText] = useState(asset.transcript ?? '')

  // Follows the asset while a transcription is running, so the text appears
  // when it lands instead of needing the panel reopened.
  useEffect(() => { setText(asset.transcript ?? '') }, [asset.transcript])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['assets', ws] })

  const save = useMutation({
    mutationFn: () => api.patch(`/api/workspaces/${ws}/assets/${asset.id}/transcript`,
      { transcript: text }),
    onSuccess: () => { invalidate(); onClose() },
  })

  // The task is watched, not just started.
  //
  // This used to invalidate on success and stop -- but "success" here means
  // the task was QUEUED, and the transcript lands seconds later on the worker.
  // The message said it would appear and then nothing ever refetched, so it
  // read as broken.
  const [taskID, setTaskID] = useState('')

  const retranscribe = useMutation({
    mutationFn: () => api.post<Task<TranscribeResult>>(
      `/api/workspaces/${ws}/assets/${asset.id}/transcribe`),
    onSuccess: t => setTaskID(t.id),
  })

  const task = useQuery({
    queryKey: ['task', ws, taskID],
    queryFn: () => api.get<Task<TranscribeResult>>(`/api/workspaces/${ws}/tasks/${taskID}`),
    enabled: !!taskID,
    // Stops polling the moment it settles, rather than asking forever.
    refetchInterval: q => {
      const s = q.state.data?.status
      return s === 'done' || s === 'failed' || s === 'cancelled' ? false : 1200
    },
  })

  const ownRunning = !!taskID && task.data?.status !== 'done'
    && task.data?.status !== 'failed' && task.data?.status !== 'cancelled'
  const running = ownRunning || !!liveTask

  useEffect(() => {
    if (task.data?.status === 'done') {
      invalidate()
      setTaskID('')
    }
    // invalidate is stable enough here: it closes over ws only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.data?.status])

  // Includes the moment between clicking Transcribe and the task id arriving,
  // so the box does not stay editable during that gap.
  const busy = running || retranscribe.isPending || save.isPending
  const dirty = text !== (asset.transcript ?? '')

  return (
    <div className="space-y-2 rounded-[--radius-card] border border-brand/30 bg-surface-1 p-4">
      {/* Named, because the panel no longer sits under the tile it belongs to. */}
      <SectionLabel>Transcript · {asset.name}</SectionLabel>

      <div className="relative">
        <Textarea
          rows={8} value={text} onChange={e => setText(e.target.value)}
          placeholder="What is said in this recording…"
          // Locked while a fresh transcription is in flight. Whatever is typed
          // in the meantime is about to be replaced by the result, so an
          // editable box here is an invitation to lose the edit.
          disabled={busy}
          className={cn('text-[13px] leading-relaxed', busy && 'opacity-50')}
        />
        {busy && (
          <div className="absolute inset-0 grid place-items-center rounded-[--radius-control] bg-surface-1/70">
            <span className="flex items-center gap-2 text-sm text-ink-dim">
              <Loader2 className="size-4 animate-spin text-brand" />
              {running ? statusLabel(task.data ?? liveTask) || 'Transcribing…' : 'Saving…'}
            </span>
          </div>
        )}
      </div>

      <p className="text-[11px] leading-relaxed text-ink-faint">
        Captions use these words exactly. Fix anything misheard — product names especially.
      </p>

      <ErrorText error={save.error} />
      <ErrorText error={retranscribe.error} />
      {/* A failed task is reported here rather than left as a spinner that
          quietly stopped. */}
      {task.data?.status === 'failed' && (
        <ErrorText error={task.data.error ?? 'Transcription failed'} />
      )}
      {task.data?.result?.silent && <Note tone="warn">{task.data.result.silent}</Note>}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary" size="sm"
          onClick={() => save.mutate()} disabled={!dirty || busy}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose} disabled={running}>
          Cancel
        </Button>
        <div className="flex-1" />

        {/* Confirmed when there is something to lose. Re-transcribing replaces
            the text outright, and a correction someone typed is exactly the
            thing a fresh pass would undo. */}
        {running ? (
          <span className="flex items-center gap-2 text-xs text-ink-faint">
            <Loader2 className="size-3.5 animate-spin text-brand" />
            Transcribing — a few seconds
          </span>
        ) : asset.transcript ? (
          <ConfirmButton
            size="sm"
            confirmLabel="Replace with a fresh transcription"
            onConfirm={() => retranscribe.mutate()}
          >
            <RefreshCw /> Re-transcribe
          </ConfirmButton>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => retranscribe.mutate()}>
            <RefreshCw /> Transcribe
          </Button>
        )}
      </div>
    </div>
  )
}

const formatDuration = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`

function NewAsset({ ws, brandID, initialKind, onDone }: {
  ws: string
  brandID: string
  initialKind: keyof typeof KINDS
  onDone: () => void
}) {
  const qc = useQueryClient()
  const kind = initialKind
  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [duration, setDuration] = useState<number | null>(null)
  const [poster, setPoster] = useState<string | null>(null)
  const [transcript, setTranscript] = useState('')
  // Transcription is started by the upload itself, so the form can show it
  // before anything is saved.
  const [transcribeTask, setTranscribeTask] = useState('')
  const [silent, setSilent] = useState(false)
  const [phoneAssetID, setPhoneAssetID] = useState('')

  const transcribing = useQuery({
    queryKey: ['task', ws, transcribeTask],
    queryFn: () => api.get<Task<TranscribeResult>>(`/api/workspaces/${ws}/tasks/${transcribeTask}`),
    enabled: !!transcribeTask,
    refetchInterval: q => {
      const st = q.state.data?.status
      return st === 'done' || st === 'failed' || st === 'cancelled' ? false : 1200
    },
  })

  const task = transcribing.data
  const transcriptReady = task?.status === 'done'
  const transcriptRunning = !!transcribeTask && !!task && !transcriptReady
    && task.status !== 'failed' && task.status !== 'cancelled'

  // Fills the box once, when the text arrives. Not on every render, or it
  // would overwrite corrections as they were typed.
  useEffect(() => {
    if (transcriptReady && task?.result) setTranscript(task.result.transcript ?? '')
  }, [transcriptReady, task?.result])

  const create = useMutation({
    mutationFn: () => phoneAssetID
      ? api.patch(`/api/workspaces/${ws}/assets/${phoneAssetID}`, { name: name.trim() })
      : api.post(`/api/workspaces/${ws}/brands/${brandID}/assets`, {
          kind, name, storage_key: key, poster_key: poster,
          duration_s: duration, transcript: transcript || null,
        }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets', ws, brandID] })
      // The task list too. Uploading a demo queues transcription server-side,
      // and the task poll only runs while it can see live work -- without this
      // it has already stopped and would not notice the new one until
      // something else happened to refetch.
      qc.invalidateQueries({ queryKey: ['tasks', ws] })
      onDone()
    },
  })

  const meta = KINDS[kind]
  const filePicker = (
    <FilePicker
      ws={ws} category={kind} label="Upload file" accept={meta.accept}
      value={key}
      onChange={(k: string, m: UploadResult) => {
        setKey(k); setDuration(m.duration_s ?? null); setPoster(m.poster_key ?? null)
        setTranscribeTask(m.transcribe_task_id ?? '')
        setSilent(!!m.silent)
        setTranscript('')
        if (!name && m.filename) setName(m.filename.replace(/\.[^.]+$/, ''))
      }}
      hint={kind === 'insert'
        ? 'Up to 500 MB. Duration is read from the file automatically.'
        : 'A screenshot straight from the phone is ideal — interface and all. It gets removed when the look is generated.'}
    />
  )

  return (
    <Card className="space-y-4 border-brand/25">
      <CardTitle>Upload asset</CardTitle>

      <div className="max-w-2xl">
        <Field label="Name" help={`How you will recognise this ${meta.label.toLowerCase()} when picking one.`}>
          <Input
            value={name} onChange={e => setName(e.target.value)}
            placeholder={kind === 'insert' ? 'Dashboard walkthrough' : 'Kitchen, morning light'}
          />
        </Field>
      </div>

      <div className="space-y-2">
        <SectionLabel>File</SectionLabel>
        {phoneAssetID ? (
          <div className="flex items-center gap-2 rounded-[--radius-control] border border-good/30 bg-good/5 px-4 py-3 text-sm text-good">
            <Film className="size-4" /> File uploaded
          </div>
        ) : kind === 'insert' && !key ? (
          <div className="grid items-stretch gap-3 sm:grid-cols-2">
            {filePicker}
            <PhoneUploadPanel ws={ws} brandID={brandID} name={name} onDone={(received, autoFinish) => {
              void qc.invalidateQueries({ queryKey: ['assets', ws, brandID] })
              void qc.invalidateQueries({ queryKey: ['tasks', ws] })
              if (autoFinish) onDone()
              else if (received.asset_id) setPhoneAssetID(received.asset_id)
            }} />
          </div>
        ) : filePicker}
      </div>

      {kind === 'insert' && key && (
        transcriptRunning ? (
          // No empty box while this runs. An editable field with nothing in it
          // reads as "type the transcript yourself", and waiting for it to
          // fill in is how two uploads got abandoned.
          <Field label="Transcript">
            <div className="flex items-center gap-2 rounded-[--radius-control] border border-line bg-surface-2 px-3 py-3 text-sm text-ink-dim">
              <Loader2 className="size-4 animate-spin text-brand" />
              {statusLabel(task) === 'Waiting for a worker…'
                ? 'Waiting for a worker…'
                : 'Generating the transcript…'}
              <span className="text-xs text-ink-faint">
                — you can save now and it will finish on its own
              </span>
            </div>
          </Field>
        ) : silent ? (
          <Note>This recording has no audio, so there is nothing to transcribe.</Note>
        ) : task?.status === 'failed' ? (
          <Field label="Transcript" help="Transcription failed, so type what is said or fix it later.">
            <Textarea rows={4} value={transcript} onChange={e => setTranscript(e.target.value)} />
          </Field>
        ) : transcript || transcriptReady ? (
          <Field
            label="Transcript"
            help="Transcribed from the audio. Captions use these words exactly, so fix anything misheard — product names especially."
          >
            <Textarea
              rows={5} value={transcript} onChange={e => setTranscript(e.target.value)}
              className="text-[13px] leading-relaxed"
            />
          </Field>
        ) : null
      )}

      {phoneAssetID && !name.trim() && (
        <Note tone="brand">File uploaded. Give this product demo a name to finish.</Note>
      )}

      <ErrorText error={create.error} />
      <div className="flex gap-2">
        <Button
          variant="primary"
          onClick={() => create.mutate()}
          disabled={(!key && !phoneAssetID) || !name || create.isPending}
        >
          {create.isPending ? 'Saving…' : phoneAssetID ? 'Save name' : 'Add asset'}
        </Button>
        <Button variant="ghost" onClick={onDone}>Cancel</Button>
        {/* Says which field is missing. A dead button with no explanation is
            how an upload ends up abandoned with the file already stored. */}
        {!create.isPending && ((!key && !phoneAssetID) || !name) && (
          <span className="self-center text-xs text-ink-faint">
            {!key && !phoneAssetID ? 'Choose a file' : 'Give it a name'}
          </span>
        )}
      </div>
    </Card>
  )
}

type PhoneSession = {
  id: string
  status: 'waiting' | 'uploading' | 'done' | 'failed'
  upload_url?: string
  expires_at: string
  filename?: string
  error?: string
  asset_id?: string
}

function PhoneUploadPanel({ ws, brandID, name, onDone }: { ws: string; brandID: string; name: string; onDone: (session: PhoneSession, autoFinish: boolean) => void }) {
  const [session, setSession] = useState<PhoneSession | null>(null)
  const [qr, setQR] = useState('')
  const [autoFinish, setAutoFinish] = useState(false)
  const create = useMutation({
    mutationFn: () => api.post<PhoneSession>(`/api/workspaces/${ws}/phone-uploads`, { brand_id: brandID, category: 'insert', name: name.trim() }),
    onSuccess: result => { setAutoFinish(!!name.trim()); setSession(result) },
  })
  const status = useQuery({
    queryKey: ['phone-upload', ws, session?.id],
    queryFn: () => api.get<PhoneSession>(`/api/workspaces/${ws}/phone-uploads/${session!.id}`),
    enabled: !!session?.id,
    refetchInterval: q => ['done', 'failed'].includes(q.state.data?.status ?? '') ? false : 1200,
  })

  useEffect(() => {
    if (!session?.upload_url) return
    QRCode.toDataURL(session.upload_url, { width: 240, margin: 1, errorCorrectionLevel: 'M' }).then(setQR)
  }, [session?.upload_url])

  useEffect(() => {
    if (status.data?.status === 'done') onDone(status.data, autoFinish)
  }, [status.data, onDone, autoFinish])

  if (!session) {
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <Button type="button" variant="secondary" className="min-h-[66px] w-full border-dashed" onClick={() => create.mutate()} disabled={create.isPending}>
          <Smartphone /> {create.isPending ? 'Preparing…' : 'Upload from phone'}
        </Button>
        <span className="text-xs text-ink-faint">Scan once; no phone sign-in needed.</span>
        <ErrorText error={create.error} />
      </div>
    )
  }

  const live = status.data ?? session
  return (
    <div className="space-y-3 rounded-[--radius-control] border border-line bg-surface-2 p-4 sm:col-span-3">
      <div className="flex flex-wrap items-center gap-5">
        {qr && <img src={qr} alt="QR code for phone upload" className="size-44 rounded bg-white p-2" />}
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm font-medium">Scan with your phone</p>
          <p className="text-xs leading-relaxed text-ink-faint">Open the camera, scan this code, then choose the recorded demo. This link expires in 15 minutes and works once.</p>
          <p className="break-all text-xs text-ink-dim">{session.upload_url}</p>
          <p className="flex items-center gap-2 text-sm text-brand">
            {live.status === 'waiting' ? <><Smartphone /> Waiting for phone…</>
              : live.status === 'uploading' ? <><Loader2 className="animate-spin" /> Uploading and processing…</>
                : live.status === 'failed' ? `Upload failed: ${live.error ?? 'try again'}` : 'Video received'}
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={() => { setSession(null); setQR('') }}>
            {live.status === 'failed' ? 'Try again' : 'Cancel'}
          </Button>
        </div>
      </div>
    </div>
  )
}
